/**
 * Kept apart from pdf.ts on purpose.
 *
 * The bill pages need to know whether to render a share button at all, on first
 * paint. Asking pdf.ts would mean importing it statically, which drags
 * html2canvas and jsPDF - about a megabyte - into the initial download for
 * every visit, defeating the dynamic import that keeps them out of it.
 *
 * This file imports nothing.
 */

/**
 * True when this device can hand a file to another app.
 *
 * Phones and tablets can; most desktop browsers cannot. Probed with a real file
 * rather than by browser name, because several browsers accept navigator.share
 * for text yet refuse it for files.
 */
export function canShareImage(): boolean {
  if (typeof navigator === 'undefined' || !navigator.canShare || !navigator.share) return false

  // A bill goes out as a picture where the device takes one and as a PDF where
  // it does not, so the button is offered if EITHER will travel. Asking about
  // only one type would hide the button on a device that could have sent the
  // other perfectly well.
  const probes: Array<[string, string]> = [
    ['bill.jpg', 'image/jpeg'],
    ['bill.pdf', 'application/pdf'],
  ]

  for (const [name, type] of probes) {
    try {
      const probe = new File([new Blob([''], { type })], name, { type })
      if (navigator.canShare({ files: [probe] })) return true
    } catch {
      // Some browsers throw instead of answering false; try the next type.
    }
  }
  return false
}
